export interface CoachingFlag {
  id: string
  type:
    | 'low-calls'
    | 'low-pull-to-app'
    | 'low-coverage'
    | 'high-fallout'
    | 'stale-pipeline'
    | 'low-transfers'
    | 'high-failed-transfers'
  description: string
}

export interface LoanOfficer {
  id: string
  name: string
  initials: string
  team: string
  cohort: string
  roleStage: string
  totalCalls: number
  totalEngagedCalls: number
  conversations60Plus: number
  totalTalkTime: number
  avgTalkTimePerCall: number
  avgAiCallScore: number
  totalTransfers: number
  transferRate: number
  qualifiedTransfers: number
  qualifiedTransferPct: number
  failedTransfers: number
  failedTransferPct: number
  transferToSubmissionPct: number
  submissionToSignaturePct: number
  activePipelineCount: number
  activePipelineValue: number
  documentsCollected: number
  docsCollectedPct: number
  sparklineData: number[]
  coachingFlags: CoachingFlag[]
}

export interface KPI {
  id: string
  label: string
  value: number
  formattedValue: string
  change: number
  trend: 'up' | 'down'
  sparklineData: number[]
}

export const mockKPIs: KPI[] = [
  {
    id: 'totalCalls',
    label: 'Total Calls',
    value: 425,
    formattedValue: '425',
    change: 8.2,
    trend: 'up',
    sparklineData: [380, 390, 400, 410, 415, 420, 425],
  },
  {
    id: 'conversations60Plus',
    label: 'Conversations 60+s',
    value: 185,
    formattedValue: '185',
    change: 5.4,
    trend: 'up',
    sparklineData: [150, 160, 165, 170, 175, 180, 185],
  },
  {
    id: 'totalTransfers',
    label: 'Total Transfers',
    value: 82,
    formattedValue: '82',
    change: 12.1,
    trend: 'up',
    sparklineData: [60, 65, 68, 72, 75, 78, 82],
  },
  {
    id: 'qualifiedTransfers',
    label: 'Qualified Transfers',
    value: 68,
    formattedValue: '68',
    change: 9.5,
    trend: 'up',
    sparklineData: [50, 55, 58, 60, 62, 65, 68],
  },
  {
    id: 'transferRate',
    label: 'Transfer Rate',
    value: 19.3,
    formattedValue: '19.3%',
    change: 2.1,
    trend: 'up',
    sparklineData: [15, 16, 17, 18, 18.5, 19, 19.3],
  },
  {
    id: 'failedTransferPct',
    label: 'Failed Transfer %',
    value: 8.5,
    formattedValue: '8.5%',
    change: -1.2,
    trend: 'down',
    sparklineData: [12, 11, 10.5, 10, 9.5, 9, 8.5],
  },
  {
    id: 'activePipelineCount',
    label: 'Active Pipeline',
    value: 145,
    formattedValue: '145',
    change: 6.8,
    trend: 'up',
    sparklineData: [120, 125, 130, 135, 138, 142, 145],
  },
  {
    id: 'activePipelineValue',
    label: 'Pipeline $',
    value: 58500000,
    formattedValue: '$58.5M',
    change: 14.5,
    trend: 'up',
    sparklineData: [45, 48, 50, 52, 54, 56, 58.5],
  },
]

export const mockLoanOfficers: LoanOfficer[] = [
  {
    id: 'lo-1',
    name: 'Sarah Jenkins',
    initials: 'SJ',
    team: 'East Coast',
    cohort: 'Cohort A',
    roleStage: 'Senior',
    totalCalls: 65,
    totalEngagedCalls: 48,
    conversations60Plus: 32,
    totalTalkTime: 245,
    avgTalkTimePerCall: 3.8,
    avgAiCallScore: 87.2,
    totalTransfers: 14,
    transferRate: 21.5,
    qualifiedTransfers: 12,
    qualifiedTransferPct: 85.7,
    failedTransfers: 1,
    failedTransferPct: 7.1,
    transferToSubmissionPct: 75.0,
    submissionToSignaturePct: 88.5,
    activePipelineCount: 24,
    activePipelineValue: 9500000,
    documentsCollected: 18,
    docsCollectedPct: 92.5,
    sparklineData: [60, 62, 65, 64, 66, 68, 68],
    coachingFlags: [],
  },
  {
    id: 'lo-2',
    name: 'Michael Chang',
    initials: 'MC',
    team: 'West Coast',
    cohort: 'Cohort B',
    roleStage: 'Junior',
    totalCalls: 42,
    totalEngagedCalls: 28,
    conversations60Plus: 15,
    totalTalkTime: 125,
    avgTalkTimePerCall: 2.9,
    avgAiCallScore: 58.4,
    totalTransfers: 6,
    transferRate: 14.2,
    qualifiedTransfers: 3,
    qualifiedTransferPct: 50.0,
    failedTransfers: 2,
    failedTransferPct: 33.3,
    transferToSubmissionPct: 45.0,
    submissionToSignaturePct: 60.0,
    activePipelineCount: 8,
    activePipelineValue: 2800000,
    documentsCollected: 5,
    docsCollectedPct: 55.0,
    sparklineData: [55, 50, 48, 45, 46, 44, 45],
    coachingFlags: [
      {
        id: 'f1',
        type: 'low-transfers',
        description: 'Transfer rate is below target (14.2% < 20%)',
      },
      {
        id: 'f2',
        type: 'high-failed-transfers',
        description: 'Failed transfer percentage is high (33.3%)',
      },
    ],
  },
  {
    id: 'lo-3',
    name: 'David Rodriguez',
    initials: 'DR',
    team: 'Central',
    cohort: 'Cohort A',
    roleStage: 'Mid-Level',
    totalCalls: 58,
    totalEngagedCalls: 42,
    conversations60Plus: 26,
    totalTalkTime: 195,
    avgTalkTimePerCall: 3.3,
    avgAiCallScore: 76.5,
    totalTransfers: 11,
    transferRate: 18.9,
    qualifiedTransfers: 9,
    qualifiedTransferPct: 81.8,
    failedTransfers: 1,
    failedTransferPct: 9.0,
    transferToSubmissionPct: 68.5,
    submissionToSignaturePct: 82.0,
    activePipelineCount: 18,
    activePipelineValue: 6200000,
    documentsCollected: 14,
    docsCollectedPct: 85.0,
    sparklineData: [58, 59, 60, 61, 62, 61, 62],
    coachingFlags: [],
  },
  {
    id: 'lo-4',
    name: 'Emily Chen',
    initials: 'EC',
    team: 'East Coast',
    cohort: 'Cohort C',
    roleStage: 'Mid-Level',
    totalCalls: 52,
    totalEngagedCalls: 36,
    conversations60Plus: 22,
    totalTalkTime: 168,
    avgTalkTimePerCall: 3.2,
    avgAiCallScore: 71.8,
    totalTransfers: 9,
    transferRate: 17.3,
    qualifiedTransfers: 7,
    qualifiedTransferPct: 77.7,
    failedTransfers: 1,
    failedTransferPct: 11.1,
    transferToSubmissionPct: 62.0,
    submissionToSignaturePct: 78.5,
    activePipelineCount: 15,
    activePipelineValue: 5100000,
    documentsCollected: 11,
    docsCollectedPct: 75.0,
    sparklineData: [50, 52, 54, 53, 55, 54, 55],
    coachingFlags: [
      {
        id: 'f3',
        type: 'low-coverage',
        description: 'Pipeline coverage is below target',
      },
    ],
  },
  {
    id: 'lo-5',
    name: 'James Wilson',
    initials: 'JW',
    team: 'West Coast',
    cohort: 'Cohort B',
    roleStage: 'Junior',
    totalCalls: 35,
    totalEngagedCalls: 20,
    conversations60Plus: 12,
    totalTalkTime: 95,
    avgTalkTimePerCall: 2.7,
    avgAiCallScore: 45.2,
    totalTransfers: 4,
    transferRate: 11.4,
    qualifiedTransfers: 2,
    qualifiedTransferPct: 50.0,
    failedTransfers: 2,
    failedTransferPct: 50.0,
    transferToSubmissionPct: 35.0,
    submissionToSignaturePct: 55.0,
    activePipelineCount: 6,
    activePipelineValue: 1900000,
    documentsCollected: 4,
    docsCollectedPct: 45.0,
    sparklineData: [45, 42, 40, 38, 35, 36, 38],
    coachingFlags: [
      {
        id: 'f4',
        type: 'low-calls',
        description: 'Total calls critically low (35 < 50)',
      },
      {
        id: 'f5',
        type: 'high-failed-transfers',
        description: 'Failed transfer rate is too high (50%)',
      },
      {
        id: 'f6',
        type: 'stale-pipeline',
        description: 'Multiple files suspended over 15 days',
      },
    ],
  },
  {
    id: 'lo-6',
    name: 'Amanda Foster',
    initials: 'AF',
    team: 'Central',
    cohort: 'Cohort A',
    roleStage: 'Lead',
    totalCalls: 72,
    totalEngagedCalls: 55,
    conversations60Plus: 38,
    totalTalkTime: 285,
    avgTalkTimePerCall: 3.9,
    avgAiCallScore: 91.3,
    totalTransfers: 18,
    transferRate: 25.0,
    qualifiedTransfers: 16,
    qualifiedTransferPct: 88.8,
    failedTransfers: 0,
    failedTransferPct: 0.0,
    transferToSubmissionPct: 82.0,
    submissionToSignaturePct: 94.0,
    activePipelineCount: 32,
    activePipelineValue: 12500000,
    documentsCollected: 26,
    docsCollectedPct: 96.0,
    sparklineData: [62, 64, 63, 65, 66, 65, 65],
    coachingFlags: [],
  },
  {
    id: 'lo-7',
    name: 'Robert Taylor',
    initials: 'RT',
    team: 'East Coast',
    cohort: 'Cohort C',
    roleStage: 'Junior',
    totalCalls: 48,
    totalEngagedCalls: 32,
    conversations60Plus: 18,
    totalTalkTime: 142,
    avgTalkTimePerCall: 2.9,
    avgAiCallScore: 63.7,
    totalTransfers: 7,
    transferRate: 14.5,
    qualifiedTransfers: 4,
    qualifiedTransferPct: 57.1,
    failedTransfers: 1,
    failedTransferPct: 14.2,
    transferToSubmissionPct: 52.0,
    submissionToSignaturePct: 68.0,
    activePipelineCount: 12,
    activePipelineValue: 3800000,
    documentsCollected: 8,
    docsCollectedPct: 62.0,
    sparklineData: [48, 50, 49, 51, 50, 49, 50],
    coachingFlags: [
      {
        id: 'f7',
        type: 'low-transfers',
        description: 'Transfer rate needs improvement (14.5%)',
      },
    ],
  },
  {
    id: 'lo-8',
    name: 'Lisa Patel',
    initials: 'LP',
    team: 'West Coast',
    cohort: 'Cohort A',
    roleStage: 'Lead',
    totalCalls: 85,
    totalEngagedCalls: 68,
    conversations60Plus: 45,
    totalTalkTime: 345,
    avgTalkTimePerCall: 4.0,
    avgAiCallScore: 94.1,
    totalTransfers: 22,
    transferRate: 25.8,
    qualifiedTransfers: 20,
    qualifiedTransferPct: 90.9,
    failedTransfers: 1,
    failedTransferPct: 4.5,
    transferToSubmissionPct: 88.0,
    submissionToSignaturePct: 96.5,
    activePipelineCount: 38,
    activePipelineValue: 15400000,
    documentsCollected: 32,
    docsCollectedPct: 98.0,
    sparklineData: [68, 70, 71, 70, 72, 73, 72],
    coachingFlags: [],
  },
]
